import { isEmailScannerUserAgent } from './scannerNoise';

// Pure decision logic for the extension install -> get-started landing event.
// Kept separate from analytics.ts so it can be unit-tested without that module's
// posthog-js / import.meta.env coupling.

export interface InstallLanding {
  extAnonId: string;
  /**
   * True when the extension flagged this as a reinstall by a returning device
   * (`&oqa_returning=1`, set when a durable storage.sync device id survived the
   * uninstall). Drives the `returning_device` property on the landing event.
   */
  returning: boolean;
}

/** The subset of `navigator` used to detect automated browsers. */
export interface NavigatorLike {
  webdriver?: boolean;
  userAgent?: string;
}

/**
 * Given the current URL query string, hostname, and navigator, decide whether
 * to fire the `extension_install_landed` event and under which anonymous id.
 * Returns null unless the extension handed off its anon id via
 * `?from=install&oqa_ext_anon=anon_...` on a non-local host in a real
 * (non-automated) browser.
 *
 * The automation gate mirrors the extension-side headless guard in
 * background/handlers/lifecycle.ts: CWS review/scanner sandboxes install the
 * extension in headless Chrome, which opens this handoff URL and fired
 * phantom landing events (319 fake "installs" from one scanner IP on
 * 2026-06-09, the day v1.2.15 published).
 */
export function resolveInstallLanding(
  search: string,
  hostname: string,
  navigatorLike: NavigatorLike = {},
): InstallLanding | null {
  if (hostname === 'localhost' || hostname === '127.0.0.1') return null;

  if (navigatorLike.webdriver === true) return null;
  if (/HeadlessChrome/i.test(navigatorLike.userAgent ?? '')) return null;
  // Corporate email scanners are the same class: they detonate an emailed
  // handoff link from a real-looking browser. This path raw-fetches PostHog and
  // never consults `initialized`, so the scanner guard in analytics.ts
  // initPostHog() does not cover it.
  if (isEmailScannerUserAgent(navigatorLike.userAgent)) return null;

  const params = new URLSearchParams(search);
  if (params.get('from') !== 'install') return null;

  const extAnonId = params.get('oqa_ext_anon');
  if (!extAnonId || !extAnonId.startsWith('anon_')) return null;

  return { extAnonId, returning: params.get('oqa_returning') === '1' };
}
